import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import MaterialTable from "material-table";
import { Button, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import DocumentIcon from "@material-ui/icons/Description";
import UndoSharpIcon from "@material-ui/icons/UndoSharp";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import Box from "@material-ui/core/Box";
import moment from "moment";
import { getLocalization } from "util/table-utils";
import { mapTableData } from "util/helpers";
import { PATHS } from "util/appConstants";
import {
  complete,
  getHandovers,
  deleteHandover,
  downloadScanCodes,
  selectHandoverStatus,
  selectHandovers,
  selectHandoversTimestamp,
} from "redux/slices/handoverSlice";
import withConfirm from "components/dialogs/delete";
import Navbar from "components/Navbar";
import DarkLayout from "components/Shared/DarkLayout";
import { selectUser } from "redux/slices/userSlice";
import { formatCustomerLabel } from "util/orders/formatCustomerLabel";
import HandoverBar from "components/Masterbar/HandoverBar";
import arrowpanel from "assets/img/arrow-panel.svg";
import EditIcon from "@material-ui/icons/Edit";
import { shouldShowScanCodesForHandover } from "util/deliveryTimingConfigUtils";

const useStyles = makeStyles({
  editIconButton: {
    background: "#6F9CEB",
    color: "#fff",
    "& .MuiSvgIcon-root": {
      color: "#fff",
    },
    borderRadius: "50%",
    width: 25,
    height: 25,
    minWidth: 25,
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      background: "#5b8fd9",
    },
  },
  qrIcon: {
    color: "#fff",
    cursor: "pointer",
    "&:hover": {
      opacity: 0.9,
    },
  },
  myCustomTooltip: {
    backgroundColor: "#93B5F0 !important",
    color: "#121212 !important",
    padding: "12px 16px !important",
    borderRadius: "4px !important",
    fontSize: "12px !important",
    fontFamily: "Roboto, sans-serif !important",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.3) !important",
  },
});

const equalColStyle = {
  cellStyle: {
    width: "14.5%",
    maxWidth: "14.5%",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    padding: "8px 12px",
  },
  headerStyle: {
    width: "14.5%",
    maxWidth: "14.5%",
    padding: "8px 12px",
  },
};

const HandoversList = ({ confirm }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const loading = useSelector(selectHandoverStatus);
  const handovers = useSelector(selectHandovers);
  const timestamp = useSelector(selectHandoversTimestamp);
  const user = useSelector(selectUser);
  const classes = useStyles();
  const openHandovers = handovers.filter((h) => h.status !== "COMPLETE");
  const completedHandovers = handovers.filter((h) => h.status === "COMPLETE");
  const isCompletedView = location.pathname === PATHS.handovers.completed;

  const canShowPrintButtons = (handover) => shouldShowScanCodesForHandover(handover);
  const canShowPdf = !user?.permissions?.handovers?.hide_pdf;

  const getHandoverStatusLabel = (status) => {
    if (status === "PENDING") {
      return t("Open");
    }
    if (status === "IN_PROGRESS") {
      return t("Handover In Progress");
    }
    if (status === "COMPLETE") {
      return t("Handover Completed");
    }
    return status || "-";
  };

  const getHandoverStartLabel = (handover) =>
    handover?.PickupTour?.SupplierLocation?.name ||
    handover?.PickupTour?.name ||
    t("Start");

  const getSafeCustomerLabel = (customer) => {
    if (!customer) return t("Customer");
    const parts = [
      customer.number,
      customer.name,
      customer.street,
      customer.street_number,
      customer.zipcode,
      customer.city,
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : t("Customer");
  };
  useEffect(() => {
    if (!loading && !timestamp) {
      dispatch(getHandovers());
    }
  }, [dispatch, handovers]);

  const callbackOnDelete = (rowData) => {
    confirm(() => dispatch(deleteHandover(rowData.id, t)), {
      description: t("Undo handover protocol? Orders will return to the orders list."),
    });
  };

  const callbackOnDeleteCompleted = (rowData) => {
    confirm(() => dispatch(deleteHandover(rowData.id, t)), {
      description: t("Undo handover protocol? Orders will return to the orders list."),
    });
  };

  const onComplete = (rowData) => {
    confirm(() => dispatch(complete(rowData.id, t)), {
      description: t(
        "Mark handover as complete? Orders will become available to continue processing"
      ),
    });
  };

  const handoverHasPickupTourOrder = (rowData) =>
    (rowData?.Orders || []).some((o) => o?.pickup_tour_id != null);

  const getDeliveryNoteNumber = (dn, order) => {
    return (
      dn?.delivery_note_number ||
      "-"
    );
  };

  const getDeliveryNotePackagesCount = (dn, order) => {
    if (Array.isArray(dn?.Packages)) return dn.Packages.length;
    if (Array.isArray(dn?.packages)) return dn.packages.length;
    if (typeof dn?.packages_count === "number") return dn.packages_count;
    if (typeof dn?.packagesCount === "number") return dn.packagesCount;
    if (typeof dn?.packages === "number") return dn.packages;
    if (typeof order?.packages === "number") return order.packages;
    return "-";
  };

  const renderOrdersPanel = (handover) => {
    if (!handover?.Orders?.length) {
      return (
        <Box px={8} py={2} color="#ADADAD" style={{ font: "normal normal normal 18px/24px Roboto" }}>
          {t("No orders")}
        </Box>
      );
    }

    const orders = handover.Orders || [];
    const groups = orders.reduce((acc, order) => {
      const id = order?.Customer?.id ?? order?.customer_id ?? "unknown";
      if (!acc[id]) {
        acc[id] = { customer: order?.Customer, orders: [] };
      }
      acc[id].orders.push(order);
      return acc;
    }, {});

    const tableRows = [];
    Object.entries(groups).forEach(([customerId, group]) => {
      const customerLabel = (() => {
        try {
          if (group.customer) return formatCustomerLabel(group.customer);
        } catch (e) {
          console.error(e);
          return getSafeCustomerLabel(group.customer);
        }
      })();

      tableRows.push({
        isStopHeader: true,
        stopName: customerLabel,
        stopAddress: "",
        customerId,
      });

      group.orders.forEach((order) => {
        const deliveryNotes =
          order?.delivery_notes ||
          order?.DeliveryNotes ||
          order?.deliveryNotes ||
          order?.delivery_notes_list ||
          [];
        const safeNotes = Array.isArray(deliveryNotes) ? deliveryNotes : [];
        const fallbackNotes =
          safeNotes.length > 0
            ? safeNotes
            : [
              {
                id: `order-${order?.id ?? order?.number ?? "unknown"}`,
                delivery_note_number:
                  order?.delivery_note_number ||
                  order?.deliveryNoteNumber ||
                  order?.number,
                Packages:
                  Array.isArray(order?.Packages)
                    ? order.Packages
                    : Array.isArray(order?.packages)
                      ? order.packages
                      : null,
              },
            ];

        fallbackNotes.forEach((dn) => {
          tableRows.push({
            isStopHeader: false,
            supplierLocation: order?.SupplierLocation?.name || "-",
            orderType: order?.order_type ? t(order.order_type[0]?.toUpperCase() + order.order_type.slice(1)) : "-",
            customerName: order?.Customer?.name || order?.customer_name || "-",
            deliveryNoteNumber: getDeliveryNoteNumber(dn, order),
            deliveryNoteId: dn?.id,
            packages: getDeliveryNotePackagesCount(dn, order),
            rawOrder: order,
          });
        });
      });
    });

    return (
      <div
        style={{
          padding: "15px",
          background: handover.tableData?.id % 2 === 0 ? " #1F1F1F " : "#525252",
          position: "relative",
        }}
      >
        <MaterialTable
          title="Orders"
          localization={getLocalization(t)}
          columns={[
            {
              title: t("Supplier Location"),
              field: "supplierLocation",
              ...equalColStyle,
              render: (row) => {
                if (row.isStopHeader) {
                  return (
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "15px",
                        padding: "6px 0",
                        display: "flex",
                        flexDirection: "column",
                        maxWidth: 520,
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span title={row.stopName || "-"}>{row.stopName || "-"}</span>
                    </div>
                  );
                }
                return (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.SupplierLocation ?? "-"}>
                      {row.supplierLocation ?? "-"}
                    </div>
                  </div>
                );
              },
            },
            {
              title: t("Order Type"),
              field: "orderType",
              ...equalColStyle,
              render: (row) => (row.isStopHeader ? null : <span style={{ cursor: "default" }}>{row.orderType}</span>),
            },
            {
              title: t("Customer"),
              field: "customerName",
              ...equalColStyle,
              render: (row) => {
                if (row.isStopHeader) return null;
                return (
                  <div style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.customerName ?? "-"}>
                    {row.customerName ?? "-"}
                  </div>
                );
              },
            },
            {
              title: t("Delivery Note Number"),
              field: "deliveryNoteNumber",
              ...equalColStyle,
              render: (row) => {
                if (row.isStopHeader) return null;
                const value = row.deliveryNoteNumber || "-";
                const hasValue = value !== "-";
                return (
                  <Tooltip
                    title={hasValue ? value : ""}
                    placement="top"
                    arrow
                    classes={{
                      tooltip: classes.myCustomTooltip,
                      arrow: classes.myCustomArrow,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: hasValue ? "help" : "default",
                      }}
                    >
                      {value}
                    </div>
                  </Tooltip>
                );
              },
            },
            {
              title: t("Packages"),
              field: "packages",
              ...equalColStyle,
              cellStyle: { ...equalColStyle.cellStyle, textAlign: "left" },
              type: "numeric",
              render: (row) =>
                row.isStopHeader ? null : (
                  <div style={{ textAlign: "left", width: "100%" }}>
                    {row.packages} {t("Pckg")}
                  </div>
                ),
            },
            {
              title: t("QR"),
              field: "edit",
              ...equalColStyle,
              render: (row) => {
                if (row.isStopHeader) return null;
                return (
                  <Tooltip
                    title={t("Edit Order")}
                    placement="left"
                    classes={{ tooltip: classes.myCustomTooltip }}
                    arrow
                  >
                    <IconButton
                      className={classes.editIconButton}
                      size="small"
                      disabled={isCompletedView}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isCompletedView) return;
                        if (!row.rawOrder?.id) return;
                        const queryParams = new URLSearchParams({
                          from: 'reorderStopsModal'
                        }).toString();
                        history.push(`${PATHS.orders.edit.replace(":id", row.rawOrder.id)}?${queryParams}`);
                      }}
                    >
                      <EditIcon fontSize="small" style={{ color: "#fff" }} />
                    </IconButton>
                  </Tooltip>
                );
              },
            },
            ...(canShowPrintButtons(handover)
              ? [
                {
                  title: t("Scan Codes"),
                  field: "scan",
                  ...equalColStyle,
                  render: (row) => {
                    if (row.isStopHeader) return null;
                    const isAtbasScanImport = row.rawOrder?.created_via === "import_atbas_scan";

                    return (
                      <Tooltip title={t("Print QR codes of packages")} placement="top" arrow classes={{ tooltip: classes.myCustomTooltip }}>
                        <span style={{ display: "inline-flex" }}>
                          <QrCode2Icon
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (isAtbasScanImport) return;
                              try {
                                const response = await fetch(
                                  `${import.meta.env.VITE_API}routes/scan-codes-flexible`,
                                  {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                                    },
                                    body: JSON.stringify({
                                      orderId: row.rawOrder?.id,
                                      deliveryNoteIds: [row.deliveryNoteId],
                                    }),
                                  }
                                );

                                if (!response.ok) throw new Error("Failed to generate QR PDF");
                                const blob = await response.blob();
                                const pdfURL = window.URL.createObjectURL(blob);
                                window.open(pdfURL);
                              } catch (err) {
                                console.error("QR PRINT ERROR:", err);
                              }
                            }}
                            className={classes.qrIcon}
                            style={{ color: "white", cursor: isAtbasScanImport ? "not-allowed" : "pointer" }}
                          />
                        </span>
                      </Tooltip>
                    );
                  },
                },
              ]
              : []),
          ]}
          data={tableRows}
          options={{
            tableLayout: "fixed",
            padding: "dense",
            cellStyle: equalColStyle.cellStyle,
            search: false,
            paging: false,
            toolbar: false,
            headerStyle: {
              display: "none",
            },
            rowStyle: (rowData) => ({
              background: "transparent",
              fontWeight: rowData.isStopHeader ? "bold" : "normal",
            }),
          }}
          style={{ background: "transparent", color: "white" }}
        />
      </div>
    );
  };

  const buildColumns = (isOpenTab) => {
    const columns = [
      {
        title: "ID",
        render: (rowData) => (
          <div
            style={{
              font: "normal normal bold 18px/24px Roboto",
              color: "#F5F5F5",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span>{moment(rowData.created_at).format("DD.MM.YYYY")}</span>
            <span>{moment(rowData.created_at).format("HH:mm")}</span>
          </div>
        ),
      },
      {
        title: "Status",
        render: (rowData) => (
          <span
            style={{
              font: "normal normal normal 18px/24px Roboto",
              color: "#F5F5F5",
            }}
          >
            {getHandoverStatusLabel(rowData.status)}
          </span>
        ),
      },
      {
        title: "Tour",
        render: (rowData) => {
          const firstOrderWithTour = (rowData.Orders || []).find((o) => o.PickupTour);
          if (!firstOrderWithTour) {
            return (
              <span
                style={{
                  font: "normal normal normal 18px/24px Roboto",
                  color: "#F5F5F5",
                }}
              >
                -
              </span>
            );
          }
          const tourName = firstOrderWithTour.PickupTour.name;
          const tourDeparture = firstOrderWithTour.pickup_departure;
          const label = tourName + (tourDeparture ? ` (${t(tourDeparture)})` : "");
          return (
            <span
              style={{
                font: "normal normal normal 18px/24px Roboto",
                color: "#F5F5F5",
              }}
            >
              {label}
            </span>
          );
        },
      },
      canShowPdf
        ? {
          title: "PDF",
          render: (rowData) => {
            return (
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_API}handovers/${rowData.id
                  }/document?taira=${localStorage.getItem("token")}`}
                target="_blank"
                color="primary"
              >
                <DocumentIcon style={{ color: "#6F9CEB" }} />
              </IconButton>
            );
          },
        }
        : null,
      {
        title: "Scan Codes",
        render: (rowData) => {
          if (!canShowPrintButtons(rowData)) {
            return null;
          }
          return (
            <Tooltip title={t("Print QR codes of packages")} placement="top" arrow>
              <IconButton
                color="primary"
                onClick={() => dispatch(downloadScanCodes(rowData.id, t))}
              >
                <QrCode2Icon className={"hovericon"} />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        title: "Undo",
        render: (rowData) => {
          if (
            moment(rowData.created_at)
              .add(24, "hours")
              .isAfter(moment())
          ) {
            return (
              <IconButton
                color="primary"
                style={{ padding: 0 }}
                onClick={() => (isOpenTab ? callbackOnDelete(rowData) : callbackOnDeleteCompleted(rowData))}
              >
                <UndoSharpIcon className={"hovericon"} />
              </IconButton>
            );
          }

          return null;
        },
      },
      isOpenTab
        ? {
          title: "Complete",
          render: (rowData) => {
            if (
              moment(rowData.created_at).add(2, "weeks").isAfter(moment())
            ) {
              if (handoverHasPickupTourOrder(rowData)) {
                return null;
              }
              return (
                <Button
                  color="primary"
                  style={{ font: "normal normal normal 18px/24px Roboto", color: "#6F9CEB" }}
                  onClick={() => onComplete(rowData)}
                >
                  {t("Mark Complete")}
                </Button>
              );
            }

            return null;
          },
        }
        : null,
    ];

    return columns.filter(Boolean);
  };

  return (
    <>
      <Navbar />
      <HandoverBar />
      <DarkLayout doublebar loading={loading}>
        <MaterialTable
          data={mapTableData(isCompletedView ? completedHandovers : openHandovers)}
          title={t(isCompletedView ? "Completed Handovers" : "Open Handovers")}
          columns={buildColumns(!isCompletedView)}
          detailPanel={[
            {
              icon: () => (
                <img
                  alt="icon"
                  src={arrowpanel}
                  style={{
                    width: "22px",
                    height: " 10px",
                    transform: " rotate(180deg)",
                  }}
                />
              ),
              openIcon: () => (
                <img
                  alt="icon"
                  src={arrowpanel}
                  style={{
                    width: "22px",
                    height: " 10px",
                  }}
                />
              ),
              render: (rowData) => renderOrdersPanel(rowData),
            },
          ]}
          localization={getLocalization(t)}
          options={{
            pageSize: 50,
            pageSizeOptions: [50, 100],
            sorting: false,
            detailPanelColumnAlignment: "right",
            paging: false,
            actionsColumnIndex: -1,
            search: true,
            searchFieldAlignment: "left",
            header: false,
            showTitle: false,
            cellStyle: {
              color: "white",
              border: "none",
              font: "normal normal normal 18px/24px Roboto",
            },
            rowStyle: (rowData) => {
              if (rowData.tableData.id % 2 === 0) {
                return { backgroundColor: " #1F1F1F " };
              } else {
                return { backgroundColor: "#525252" };
              }
            },
          }}
        />
      </DarkLayout>
    </>
  );
};

export default withConfirm(HandoversList);
